import type { CentralIconName } from "@central-icons-react/all/icons";
import { createElement, type ReactNode, type SVGProps } from "react";

// Only the icons the app references, in the variants it uses, parsed into
// React-ready nodes at build time by scripts/icon-registry (regenerated by
// `next dev` / `next build`, or `npm run gen:icons`). The vendor's runtime
// registry (@central-icons-react/all) is a 54 MB module and is never imported
// at runtime — the type import above is erased.
import registryJson from "./icon-registry.generated.json";

export type IconName = CentralIconName;
export type IconJoin = "round" | "square";
export type IconFill = "filled" | "outlined";
export type IconRadius = "0" | "1" | "2" | "3";
export type IconStroke = "1" | "1.5" | "2";

export type IconProps = Omit<SVGProps<SVGSVGElement>, "fill" | "stroke"> & {
  name: IconName;
  join?: IconJoin;
  fill?: IconFill;
  radius?: IconRadius;
  stroke?: IconStroke;
  size?: string | number;
  ariaHidden?: boolean;
  /** Rendered as an SVG <title> when the icon is not aria-hidden. */
  ariaLabel?: string;
  mode?: "masked" | "raw";
  maskId?: string;
};

type IconNode = [tag: string, attrs: Record<string, string>, children?: IconNode[]];
type IconRegistry = {
  meta: { source: string; icons: number; entries: number };
  variants: Record<string, Record<string, IconNode[] | undefined> | undefined>;
};

// The JSON module's inferred type widens the tuples; the generator guarantees the shape.
const registry = registryJson as unknown as IconRegistry;

export const DEFAULT_ICON_VARIANT = "round-outlined-radius-2-stroke-1.5";

const warned = new Set<string>();
function warnOnce(key: string, message: string) {
  if (process.env.NODE_ENV === "production" || warned.has(key)) return;
  warned.add(key);
  console.error(message);
}

function resolveGlyph(variant: string, name: string): IconNode[] | undefined {
  const exact = registry.variants[variant]?.[name];
  if (exact) return exact;
  const fallback = registry.variants[DEFAULT_ICON_VARIANT]?.[name];
  warnOnce(
    `${variant}/${name}`,
    fallback
      ? `[Icon] "${name}" is not generated for variant "${variant}"; rendering the default variant instead. ` +
          "Run `npm run gen:icons` (or restart `next dev`). If it persists, the variant reaches <Icon> through a prop " +
          "the inventory cannot trace — see scripts/icon-registry/inventory.mjs."
      : `[Icon] "${name}" is not in the generated icon registry. Run \`npm run gen:icons\` or restart \`next dev\`.`,
  );
  return fallback;
}

function renderNode([tag, attrs, children]: IconNode, key: number): ReactNode {
  return createElement(tag, { key, ...attrs }, children?.map(renderNode));
}

export const Icon = ({
  name,
  join = "round",
  fill = "outlined",
  radius = "2",
  stroke = "1.5",
  size = 24,
  ariaHidden = true,
  ariaLabel,
  // Plain inline nodes. `masked` paints the glyph through an SVG <mask> whose
  // id is derived from variant + name, so repeated icons emit duplicate ids and
  // some engines fail to resolve the mask (blank icon). Raw mode is the
  // rendering the app has always used; masked stays reachable per call site.
  mode = "raw",
  maskId,
  color,
  style,
  ...rest
}: IconProps) => {
  const variant = `${join}-${fill}-radius-${radius}-stroke-${stroke}`;
  const glyph = resolveGlyph(variant, name)?.map(renderNode) ?? null;
  const dimension = typeof size === "number" ? `${size}px` : size;
  const id = maskId ?? `${variant}-${name}`;

  return (
    <svg
      {...rest}
      aria-hidden={ariaHidden}
      role={ariaHidden ? undefined : "img"}
      width={dimension}
      height={dimension}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ color, ...style }}
    >
      {ariaLabel && !ariaHidden ? <title>{ariaLabel}</title> : null}
      {mode === "masked" ? (
        <>
          <mask id={id} maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
            <rect width="24" height="24" fill="#000" />
            <g fill="none" style={{ color: "#fff" }}>
              {glyph}
            </g>
          </mask>
          <rect width="24" height="24" fill="currentColor" mask={`url(#${id})`} />
        </>
      ) : (
        glyph
      )}
    </svg>
  );
};
